import React from 'react';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface NoPostsCardProps {
  title?: string;
  message?: string;
  icon?: 'image' | 'calendar' | 'list' | 'board';
  isFiltered?: boolean;
}

export const NoPostsCard: React.FC<NoPostsCardProps> = ({
  title = 'Your content gallery is empty',
  message = 'Time to showcase your creativity! Create your first post and watch your social media presence grow.',
  icon = 'image',
  isFiltered = false
}) => {
  const navigate = useNavigate();

  // Use the filtered message if filters are applied
  const displayMessage = isFiltered
    ? "We couldn't find any posts that match your current filters. Try adjusting your filter settings or create something new."
    : message;

  // Return different icons based on the view type
  const renderIcon = () => {
    switch (icon) {
      case 'calendar':
        return (
          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'list':
        return (
          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'board':
        return (
          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case 'image':
      default:
        return (
          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-12 text-center">
      <div className="flex flex-col items-center">
        <div className="mb-4 w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
          {renderIcon()}
        </div>
        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
        <p className="mt-3 text-base text-gray-500 max-w-md mx-auto">
          {displayMessage}
        </p>
        <button
          onClick={() => navigate('/posts/new')}
          className="mt-8 inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Your First Post
        </button>
      </div>
    </div>
  );
}; 